import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useAuth } from './AuthContext';

const WebSocketContext = createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
};

const getWebSocketUrl = () => {
  // Vite environment variable
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL;
  }

  if (typeof window !== 'undefined' && window.location) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // In development, connect to analytics-service directly
    if (window.location.port === '3000' && import.meta.env.DEV) {
      return 'ws://localhost:9103/ws';
    }
    return `${protocol}//${window.location.host}/ws`;
  }

  return 'ws://localhost:9103/ws';
};

export const WebSocketProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [socket, setSocket] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [subscribers, setSubscribers] = useState(new Map());
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  // Real-time data states
  const [realtimeMetrics, setRealtimeMetrics] = useState(null);
  const [realtimeCDRs, setRealtimeCDRs] = useState([]);
  const [realtimeAlerts, setRealtimeAlerts] = useState([]);
  const [networkStatus, setNetworkStatus] = useState(null);

  useEffect(() => {
    if (isAuthenticated && user) {
      connectWebSocket();
    } else {
      disconnectWebSocket();
    }

    return () => {
      disconnectWebSocket();
    };
  }, [isAuthenticated, user]);

  const connectWebSocket = () => {
    if (socket?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const wsUrl = getWebSocketUrl();
      const newSocket = new WebSocket(wsUrl);

      newSocket.onopen = () => {
        console.log('WebSocket connected');
        setConnectionStatus('connected');
        setSocket(newSocket);
        reconnectAttemptsRef.current = 0;
        
        // Send authentication and subscribe to initial channels
        newSocket.send(JSON.stringify({
          type: 'auth',
          token: localStorage.getItem('authToken')
        }));

        // Subscribe to default channels
        subscribeToChannel('metrics');
        subscribeToChannel('cdrs');
        subscribeToChannel('alerts');
        subscribeToChannel('network_status');
      };

      newSocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      newSocket.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        setConnectionStatus('disconnected');
        setSocket(null);
        
        // Attempt to reconnect if not a clean close
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          scheduleReconnect();
        }
      };

      newSocket.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionStatus('error');
      };

    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      setConnectionStatus('error');
    }
  };

  const disconnectWebSocket = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (socket) {
      socket.close(1000, 'User logout');
      setSocket(null);
    }
    setConnectionStatus('disconnected');
  };

  const scheduleReconnect = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
    reconnectAttemptsRef.current++;
    
    setConnectionStatus('reconnecting');
    
    reconnectTimeoutRef.current = setTimeout(() => {
      if (isAuthenticated) {
        connectWebSocket();
      }
    }, delay);
  };

  const handleWebSocketMessage = (data) => {
    const { type, channel, payload } = data;

    switch (type) {
      case 'metrics_update':
        setRealtimeMetrics(payload);
        break;
      
      case 'new_cdr':
        setRealtimeCDRs(prev => [payload, ...prev.slice(0, 99)]); // Keep latest 100
        break;
      
      case 'alert':
        setRealtimeAlerts(prev => [payload, ...prev.slice(0, 49)]); // Keep latest 50
        break;
      
      case 'network_status':
        setNetworkStatus(payload);
        break;
      
      case 'bulk_update':
        if (payload.metrics) setRealtimeMetrics(payload.metrics);
        if (payload.cdrs) setRealtimeCDRs(payload.cdrs.slice(0, 100));
        if (payload.alerts) setRealtimeAlerts(payload.alerts.slice(0, 50));
        if (payload.network_status) setNetworkStatus(payload.network_status);
        break;

      default:
        // Handle custom subscribers
        subscribers.forEach((callback, subscriberId) => {
          if (callback && typeof callback === 'function') {
            callback(data);
          }
        });
    }
  };

  const subscribeToChannel = (channel) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({
        type: 'subscribe',
        channel: channel
      }));
    }
  };

  const unsubscribeFromChannel = (channel) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({
        type: 'unsubscribe',
        channel: channel
      }));
    }
  };

  const sendMessage = (message) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(message));
      return true;
    }
    return false;
  };

  const subscribe = (callback) => {
    const subscriberId = Date.now() + Math.random();
    setSubscribers(prev => new Map(prev).set(subscriberId, callback));
    
    return () => {
      setSubscribers(prev => {
        const newMap = new Map(prev);
        newMap.delete(subscriberId);
        return newMap;
      });
    };
  };

  const requestRealTimeUpdate = (type, params = {}) => {
    return sendMessage({
      type: 'request_update',
      data_type: type,
      params: params
    });
  };

  const value = {
    // Connection state
    socket,
    connectionStatus,
    isConnected: connectionStatus === 'connected',
    
    // Real-time data
    realtimeMetrics,
    realtimeCDRs,
    realtimeAlerts,
    networkStatus,
    
    // Methods
    subscribeToChannel,
    unsubscribeFromChannel,
    sendMessage,
    subscribe,
    requestRealTimeUpdate,
    connectWebSocket,
    disconnectWebSocket,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketProvider;
